import { FC } from 'react';
import clsx from 'clsx';
import styles from './InvoiceSummary.module.scss';

import { MerchantCurrency } from 'components/MerchantCurrency';
import { SummaryLayout } from 'components/SummaryLayout';
import { SummaryItem } from 'components/SummaryItem';
import { Trans, useTranslation } from 'react-i18next';

import { CHECKOUT_TEST_ID } from '../../const';
import { useCart } from 'hooks/context/cart';
import { useOrderTotals } from 'routes/Checkout/context/useOrderTotals';
import { useServiceType } from 'hooks/context/serviceType';
import { useRunningOrder } from 'hooks/context/RunningOrder';
import { Tooltip } from '@clover/web-lib-core/components';

type InvoiceSummaryProps = {
  showTax?: boolean;
  tipsEnabled?: boolean;
}

export const InvoiceSummary: FC<InvoiceSummaryProps> = ({ showTax, tipsEnabled }) => {
  const { t } = useTranslation();
  const { isDelivery } = useServiceType();
  const { subtotal, tax, tip, totalWithTip, surcharge, serviceCharge, discount, deliveryFee, orderFee, giftCard
  } = useOrderTotals();
  const { subtotal: itemSubtotal } = useCart();
  const runningOrder = useRunningOrder();

  return (
    <SummaryLayout>
      <SummaryItem title={t('checkoutPage.subtotal')} amount={subtotal} data-testid={CHECKOUT_TEST_ID.subtotal} />

      { discount < 0 &&
        <SummaryItem>
          <dt className={clsx(styles.blue)}>{t('checkoutPage.perk')}</dt>
          <dd className={styles.blue}>
            <Trans i18nKey="perks.discountWrapper" >
              (<MerchantCurrency
                  data-testid={CHECKOUT_TEST_ID.discount}
                  amount={discount} />)
            </Trans>
          </dd>
        </SummaryItem>
        }

      {showTax && (
        <SummaryItem title={t('checkoutPage.tax')} amount={tax} data-testid={CHECKOUT_TEST_ID.tax} />
      )}

      { serviceCharge > 0 && (
        <SummaryItem title={t('checkoutPage.serviceCharge')} amount={serviceCharge} data-testid={CHECKOUT_TEST_ID.serviceCharge} />
      )}

      {(tipsEnabled || isDelivery) && (itemSubtotal > 0 || runningOrder.hasItems) &&
        <SummaryItem title={t('checkoutPage.tip')} amount={tip} data-testid={CHECKOUT_TEST_ID.tip} />
      }

      {isDelivery && (
        <SummaryItem title={t('checkoutPage.deliveryFee')} amount={deliveryFee} data-testid={CHECKOUT_TEST_ID.deliveryFee} />
      )}

      {!!orderFee && (
         <SummaryItem titleCss={clsx(styles.tooltipSummary)} titleToolTip={<Tooltip className={clsx(styles.tooltip)} placement="top" glyph="help" label={`${t('checkoutPage.orderFeeDesc')}`}></Tooltip>} title={t('checkoutPage.orderFee')} amount={orderFee} data-testid={CHECKOUT_TEST_ID.orderFee} />
      )}

      {orderFee === 0 && (
         <SummaryItem titleCss={clsx(styles.tooltipSummary)} titleToolTip={<Tooltip className={clsx(styles.tooltip)} placement="top" glyph="help" label={`${t('checkoutPage.orderFeeDesc')}`}></Tooltip>} title={t('checkoutPage.orderFee')} amount={0.00} data-testid={CHECKOUT_TEST_ID.orderFee} />
      )}

      {giftCard && giftCard > 0 && (
         <SummaryItem title={t('checkoutPage.giftCard')} amount={-giftCard} data-testid={CHECKOUT_TEST_ID.giftCard} titleCss={clsx(styles.giftColor)} amountCss={styles.giftColor} />
      )}

      <SummaryItem title={t('checkoutPage.total')} amount={totalWithTip} data-testid={CHECKOUT_TEST_ID.total} titleCss={clsx(styles.bold)} amountCss={styles.bold} />

      {surcharge > 0 &&
        <SummaryItem>
        <dt className={clsx(styles.italic)} data-testid={CHECKOUT_TEST_ID.surcharge}>{t('checkoutPage.surcharge', { percentage : surcharge })}</dt>
      </SummaryItem>
      }
    </SummaryLayout>
  );
};
